import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { PosScreenClassComponent, DialogIcon, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import { Result, ValidationErrors, Validator } from 'core/infrastructure';
import { Res } from 'core/api';
import { Cols, Col } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';

type Props = {
	maxMenuQuantityConfirmation: number;
} & Modal.Actions<number>;

type State = { quantity: Nullable<number> };

class EditQuantity extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<State>>();
	private quantityInputBox = React.createRef<UI.IntBox<State>>();

	createInitialState(_props: Modal.Actions<number>): Nullable<State> {
		return { quantity: null };
	}

	onComponentDidMount(): void {
		this.quantityInputBox.current!.activate();
	}

	onComponentWillUnmount(): void { }

	protected getScreenId(): string {
		return 'GET_MENU_QUANTITY';
	}

	protected getTitle(): string {
		return Res.Web.COMPOSE_MENU();
	}

	protected getImage(): string | SvgIcon {
		return Images.ComposeMenu;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOK });
	}

	private async confirmQuantityIfExceedsMax(menuQuantity: Nullable<number>): Promise<boolean> {
		if (menuQuantity && menuQuantity > this.props.maxMenuQuantityConfirmation) {
			const confirmationResult = await Message.confirm(Res.Msg.MSG_MENU_QUANTITY_CONFIRMATION(menuQuantity));
			return confirmationResult.ok;
		}

		return true;
	}

	private onOK = async () => {
		const valid = await this.form.current!.updateModel();
		if (valid && await this.confirmQuantityIfExceedsMax(this.state.quantity)) {
			this.close(this.state.quantity!);
		}
	};

	protected getContent(): React.ReactNode {
		const { Form, IntBox } = UI.controlsFor<State>();

		const validate = (state: State) => {
			const errors: ValidationErrors = {};

			if (state.quantity === null || state.quantity <= 0)
				ValidationErrors.add(errors, 'quantity', Res.Msg.MSG_QUANTITY_MUST_BE_GREATER_THAN_ZERO());

			return errors;
		};

		return <Form small ref={this.form} model={this.state} validator={Validator.create(validate)}>
			<DialogIcon src={Images.ComposeMenu} />
			<Cols>
				<Col width='*' className='m-t-md'>
					<IntBox className='form-control text-center' placeholder={Res.Web.COMPOSE_MENU_MENU_QTY_LBL()} linkTo={x => x.quantity} allowNegative={false} autoComplete='off' ref={this.quantityInputBox} required onKeyDown={UI.whenEnterKey(this.onOK)} />
				</Col>
			</Cols>
		</Form>;
	}
}

export default async function getMenuQuantity(maxMenuQuantityConfirmation = 99): Promise<Result<number>> {
	const result = await Modal.show(EditQuantity, { maxMenuQuantityConfirmation });
	return result.ok ? Result.ok(result.value) : Result.error();
}
